<main class="min-h-screen bg-gradient-to-br from-gray-50 to-white px-4 py-8">
  <div class="max-w-4xl mx-auto">
    <!-- Header -->
    <div class="mb-8 text-center">
      <h1 class="text-2xl font-semibold text-primary-600 mb-2">Photo Capture</h1>
      <p class="text-sm font-normal text-gray-500 dark:text-slate-400">{{ identityNumber || 'User' }}</p>
    </div>

    <!-- Error Message -->
    @if (errorMessage) {
      <div class="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
        <p class="text-sm font-normal text-red-600">{{ errorMessage }}</p>
      </div>
    }

    <!-- Camera View -->
    @if (!capturedImage) {
      <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-md p-8 border border-gray-100">
        @if (!isCapturing) {
          <div class="text-center py-12">
            <div class="w-20 h-20 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg class="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Ready to Capture Photo</h3>
            <p class="text-sm font-normal text-gray-500 dark:text-slate-400 mb-8">Click the button below to start your camera</p>
            <button
              (click)="startCamera()"
              class="px-8 py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium text-sm rounded-xl transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 mx-auto group"
            >
              <svg class="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
              Start Camera
            </button>
          </div>
        } @else {
          <div class="space-y-6">
            <div class="flex justify-center">
              <div class="relative bg-black rounded-xl overflow-hidden shadow-lg w-full max-w-2xl" style="aspect-ratio: 4/3;">
                <video
                  #videoElement
                  autoplay
                  playsinline
                  class="w-full h-full object-cover"
                  style="transform: scaleX(-1);"
                ></video>
                @if (isCapturing) {
                  <div class="absolute inset-0 border-4 border-primary-500 border-dashed pointer-events-none rounded-xl"></div>
                }
              </div>
            </div>
            <canvas #canvasElement style="display: none;"></canvas>
            <div class="flex justify-center gap-3">
              <button
                (click)="captureWithLightingCheck()"
                class="px-8 py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium text-sm rounded-xl transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 group"
              >
                <svg class="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                Capture
              </button>
              <button
                (click)="stopCamera()"
                class="px-6 py-3.5 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-800 hover:border-gray-300 dark:border-slate-600 transition-all duration-200 font-medium text-sm shadow-sm hover:shadow-md flex items-center gap-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                Cancel
              </button>
            </div>
            <p class="text-xs text-center font-normal text-gray-500 dark:text-slate-400">
              Ensure you are in a well-lit area. The photo will be cropped to 640x480 pixels and compressed to under 1MB.
            </p>
          </div>
        }
      </div>
    } @else {
      @if (isVerified) {
        <!-- Verification Success Section -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-md p-8 border border-gray-100">
          <div class="text-center py-12">
            <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Verification Successful</h3>
            <p class="text-sm font-normal text-gray-500 dark:text-slate-400">Your identity has been successfully verified</p>
          </div>
        </div>
      } @else {
        <!-- Photo Review Section -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-md p-8 border border-gray-100">
          <div class="text-center mb-8">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">Review Your Photo</h3>
            <p class="text-sm font-normal text-gray-500 dark:text-slate-400">Please review your photo before submitting</p>
          </div>
          
          <div class="space-y-6">
            <!-- Photo Preview -->
            <div class="flex justify-center">
              <div class="relative bg-gray-50 dark:bg-slate-800 rounded-xl overflow-hidden shadow-lg w-full max-w-2xl border-2 border-gray-200 dark:border-slate-700" style="aspect-ratio: 4/3;">
                <img [src]="capturedImage" alt="Captured photo" class="w-full h-full object-contain">
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex justify-center gap-3 pt-2">
              <button
                (click)="retakePhoto()"
                class="px-6 py-3.5 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-800 hover:border-gray-300 dark:border-slate-600 transition-all duration-200 font-medium text-sm shadow-sm hover:shadow-md flex items-center gap-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Retake Photo
              </button>
              <button
                (click)="submitPhoto()"
                [disabled]="isSubmitting"
                class="px-8 py-3.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium text-sm rounded-xl transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                @if (isSubmitting) {
                  <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Submitting...</span>
                } @else {
                  <svg class="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Submit Photo</span>
                }
              </button>
            </div>
          </div>
        </div>
      }
    }
  </div>
</main>
